/**
 * PersonOverviewOutputsSection — books, memorial, QR, and export tools grouped lower on Overview.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  ChevronRight,
  Download,
  Flame,
  Frame,
  Loader2,
  QrCode,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import {
  isMemorialActive,
  memorialManageHref,
} from '../../utils/memorial';
import { storybookApi, storybookHref, storybookStartHref } from '../../utils/storybook';
import { legacyBookPreviewHref } from '../../utils/legacyBookPreview';
import { HelpContextLink } from '../help';
import { PersonMemorialOverviewCard } from '../memorial/PersonMemorialOverviewCard';
import { MemorialProfileBanner } from '../memorial/MemorialProfileBanner';
import { MemorialQRPanel } from '../memorial/MemorialQRPanel';
import { CORE_JOURNEY_UI } from '../../config/testerRelease';

function OutputRow({ icon: Icon, title, description, onClick, href, external, testId, disabled, trailing }) {
  const inner = (
    <>
      <div className="flex min-w-0 items-start gap-3">
        {Icon ? (
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(38_14%_92%)]/80 text-[#8b7355]">
            <Icon className="h-4 w-4" aria-hidden />
          </span>
        ) : null}
        <div className="min-w-0">
          <p className="font-serif text-sm text-[hsl(24_12%_20%)]">{title}</p>
          {description ? (
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground/85">{description}</p>
          ) : null}
        </div>
      </div>
      {trailing || <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden />}
    </>
  );

  const className = cn(
    'flex w-full items-center justify-between gap-3 rounded-xl border border-[hsl(38_14%_86%)]/55 bg-[hsl(42_28%_99%)]/70 px-4 py-3.5 text-left transition',
    !disabled && 'hover:border-[hsl(38_18%_78%)]/65 hover:bg-white/85',
    disabled && 'opacity-60',
  );

  if (href) {
    if (external) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={className}>
          {inner}
        </a>
      );
    }
    return (
      <Link to={href} data-testid={testId} className={className}>
        {inner}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} className={className}>
      {inner}
    </button>
  );
}

export function PersonOverviewOutputsSection({
  person,
  treeId,
  canEdit = false,
  isGenealogical = true,
  canManageMemorial = false,
  onExportPdf,
  exportingPdf = false,
  onNavigate,
  personStories = [],
  storyCount = 0,
  documentCount = 0,
  onManageTribute,
}) {
  const [storybook, setStorybook] = useState(null);
  const [storybookLoading, setStorybookLoading] = useState(true);

  useEffect(() => {
    if (!person?.id || !treeId) {
      setStorybook(null);
      setStorybookLoading(false);
      return;
    }
    setStorybookLoading(true);
    storybookApi
      .list({ treeId, personId: person.id })
      .then((list) => setStorybook(list[0] || null))
      .catch(() => setStorybook(null))
      .finally(() => setStorybookLoading(false));
  }, [person?.id, treeId]);

  if (!person) return null;

  const storybookLink = storybook
    ? storybookHref(storybook.id)
    : storybookStartHref({ treeId, personId: person.id, type: 'person' });

  const storybookDescription = storybookLoading
    ? 'Checking progress…'
    : storybook
      ? `${storybook.completed_count ?? 0} of ${storybook.total_prompts ?? 16} prompts answered`
      : 'Guided life-story chapters when you are ready';

  const memorialActive = isMemorialActive(person);

  const showStorybook = !CORE_JOURNEY_UI && isGenealogical && (canEdit || storybook);
  const showMemorialOutputs = !CORE_JOURNEY_UI && isGenealogical;
  const showMemorialIfActive = CORE_JOURNEY_UI && isGenealogical && memorialActive;
  const showExport = typeof onExportPdf === 'function';

  if (!showStorybook && !showMemorialOutputs && !showMemorialIfActive && !showExport) return null;

  return (
    <section className="space-y-5 border-t border-[hsl(38_14%_86%)]/50 pt-8" data-testid="person-overview-outputs">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground/55">
          {CORE_JOURNEY_UI ? 'Legacy Book' : 'Outputs & sharing'}
        </p>
        <h2 className="mt-1 font-serif text-lg font-light text-[hsl(24_12%_18%)]">
          {CORE_JOURNEY_UI ? 'Preserve this for your family' : 'Create from this archive'}
        </h2>
        <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-muted-foreground/85">
          {CORE_JOURNEY_UI
            ? 'Preview how photos and stories could become a Legacy Book — a keepsake your family can hold onto.'
            : 'Books, memorial pages, and keepsakes built from what you have already preserved — when the time feels right.'}
        </p>
        <p className="mt-2">
          <HelpContextLink section="person-profiles">How to complete a profile</HelpContextLink>
          <span className="mx-2 text-muted-foreground/40" aria-hidden>·</span>
          <HelpContextLink section="legacy-book">Legacy Book guide</HelpContextLink>
        </p>
      </div>

      {memorialActive ? (
        <MemorialProfileBanner person={person} onManageTribute={onManageTribute} />
      ) : null}

      <div className="grid gap-3">
        {showExport ? (
          <OutputRow
            icon={BookOpen}
            title="Preview Legacy Book"
            description={CORE_JOURNEY_UI ? 'See how memories gather into a book for your family.' : 'See how this profile could become a Legacy Book. Export-ready design coming next.'}
            href={legacyBookPreviewHref(person)}
            testId="overview-output-legacy-book"
          />
        ) : null}

        {showStorybook ? (
          <OutputRow
            icon={BookOpen}
            title={storybook ? 'Open Storybook' : 'Start Storybook'}
            description={storybookDescription}
            href={storybookLink}
            testId="overview-output-storybook"
          />
        ) : null}

        {showMemorialIfActive ? <PersonMemorialOverviewCard person={person} /> : null}

        {showMemorialOutputs ? <PersonMemorialOverviewCard person={person} /> : null}

        {showMemorialOutputs && memorialActive ? (
          <OutputRow
            icon={Frame}
            title="Memorial Photo Link preview"
            description="Design a framed memorial photo link from your memorial workspace."
            href={memorialManageHref(person)}
            testId="overview-output-photo-link"
          />
        ) : null}

        {showExport ? (
          <OutputRow
            icon={Download}
            title="Export person PDF"
            description="Download a PDF snapshot of this profile for review or sharing."
            onClick={() => onExportPdf('review')}
            disabled={exportingPdf}
            testId="overview-output-export-pdf"
            trailing={
              exportingPdf ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden />
              )
            }
          />
        ) : null}
      </div>

      {memorialActive && canManageMemorial ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <QrCode className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden />
            <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground/55">QR &amp; sharing</p>
          </div>
          <MemorialQRPanel personId={person.id} canManage={canManageMemorial} stories={personStories} />
        </div>
      ) : null}

      {showMemorialOutputs && !memorialActive && canEdit && !CORE_JOURNEY_UI ? (
        <p className="text-xs leading-relaxed text-muted-foreground/75">
          <Flame className="mr-1 inline h-3.5 w-3.5 text-[#8b7355]/70" aria-hidden />
          Memorial mode can be set up privately whenever your family is ready — it does not publish automatically.
        </p>
      ) : null}

      {typeof onNavigate === 'function' ? (
        <div className="pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto rounded-full px-0 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => onNavigate('stories')}
          >
            Capture more in Stories &amp; voice
            <ChevronRight className="ml-0.5 h-3 w-3 opacity-50" aria-hidden />
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export default PersonOverviewOutputsSection;
